import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "wiz-front-shared/components/button/Button";
import { Input, InputInfoMsg } from "components/common/input/Input";
import { RadioGroup, RadioItem } from "components/common/radioGroup/RadioGroup";
import { SelectBox, SelectBoxItem, SelectBoxRef } from "wiz-front-shared/components/selectBox/SelectBox";
import { toDanalCtn, toNationalNumber } from "utils/phoneNumber";
import "./Identity.css";

export type Nationality = "KOREAN" | "FOREIGNER";

export interface IdentityNameTelecomPhoneResult {
    name: string;
    telecom: string;
    phone: string;            // 하이픈 제외 숫자
    nationality: Nationality; // KR 다날 내/외국인 (1~4 / 5~8 매핑)
}

interface Props {
    initialName?: string;
    initialTelecom?: string;
    initialPhone?: string;
    initialNationality?: Nationality;


    /** 14세 미만 보호자 인증 모드 (SignUp 전용) — title 변경 + 본인 ctn 과 다른지 검증 */
    isParentMode?: boolean;
    /** 보호자 모드일 때, 본인 ctn (같은 번호 입력 방지) */
    selfCtn?: string;

    /** 국적 라디오 노출 여부 (SignUp/Recover KR 경로 전용 — 다날 genderDigit 1~8). 기본 false. */
    enableNationality?: boolean;

    /** 상단 타이틀 i18n key (기본 SignUp 용). isParentMode=true 일 때는 무시되고 titleUnder14 사용. */
    titleI18nKey?: string;
    /** 상단 안내 i18n key (기본 SignUp 용) — Recovery 등에서 override */
    infoI18nKey?: string;

    /** 입력값 변경 시마다 호출 — sideStep 도중 이탈해도 복원되도록 부분 저장용 */
    onValuesChange?: (values: IdentityNameTelecomPhoneResult) => void;

    /** 다음 버튼 콜백 — 최종 값을 직접 전달 (race-free) */
    onNext: (result: IdentityNameTelecomPhoneResult) => void;
}

/**
 * 본인인증 - 이름 + 통신사 + 전화번호 (KR / 다날)
 *
 * sideStep 점진 표시: 이름 → 통신사 → 전화번호 순으로 노출.
 * State 는 form 내부에서만 관리. 부모는 `onNext(result)` 로 최종 값 수령.
 */
export const IdentityNameTelecomPhoneForm = (props: Props) => {
    const { t } = useTranslation();

    const {
        initialName,
        initialTelecom,
        initialPhone,
        initialNationality = "KOREAN",
        isParentMode = false,
        selfCtn,
        enableNationality = false,
        titleI18nKey = "signUp.step3.title",
        infoI18nKey = "signUp.step3.info",
        onValuesChange,
        onNext,
    } = props;

    const carriers: SelectBoxItem[] = [
        { name: t("signUp.step3.skt"), value: "SKT" },
        { name: t("signUp.step3.ktf"), value: "KTF" },
        { name: t("signUp.step3.lgt"), value: "LGT" },
        { name: t("signUp.step3.skm"), value: "SKM" },
        { name: t("signUp.step3.ktm"), value: "KTM" },
        { name: t("signUp.step3.lgm"), value: "LGM" },
    ];

    // 재진입 시 진행한 단계까지 sideStep 점프.
    //   통신사가 선택돼 있으면 다음은 전화번호 단계(3) — 전화번호 미입력이어도 통신사 모달을
    //   다시 열지 않고 전화번호 입력란에 포커스되도록 telecom 유무를 기준으로 판정.
    const initialSideStep = initialTelecom ? 3 : (initialName ? 2 : 1);
    const [sideStep, setSideStep] = useState(initialSideStep);
    const [btnName, setBtnName] = useState(initialSideStep === 3 ? t("signUp.btnSMS") : t("signUp.btnNext"));

    const [nameVal, setNameVal] = useState(initialName || "");
    const [telecomItem, setTelecomItem] = useState<SelectBoxItem | undefined>(
        carriers.find(c => c.value === initialTelecom)
    );
    // 이 폼은 KR 다날 전용 — 저장 형식("1012345678")을 그대로 뿌리면 사용자가 자기 번호로
    //   알아보기 어려우므로 국내 표기로 되돌려 채운다. 제출 시 다시 저장 형식으로 수렴한다.
    const [phoneValue, setPhoneValue] = useState(toDanalCtn(initialPhone || ""));
    const [nationality, setNationality] = useState<Nationality>(initialNationality);

    const nationalityItems: RadioItem[] = [
        { label: t("signUp.step3.korean"), value: "KOREAN" },
        { label: t("signUp.step3.foreigner"), value: "FOREIGNER" },
    ];

    const nameRef = useRef<HTMLInputElement>(null);
    const phoneRef = useRef<HTMLInputElement>(null);
    const telecomSelRef = useRef<SelectBoxRef>(null);

    const [nameInfoMsg, setNameInfoMsg] = useState<InputInfoMsg>();
    const [telecomInfoMsg, setTelecomInfoMsg] = useState<InputInfoMsg>();
    const [phoneInfoMsg, setPhoneInfoMsg] = useState<InputInfoMsg>();

    const [isNameValid, setIsNameValid] = useState(false);
    const [isTelecomValid, setIsTelecomValid] = useState(false);
    const [isPhoneValid, setIsPhoneValid] = useState(false);
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    // sideStep 전환 시 자동 포커스 — Input 컴포넌트의 onFocus 가 keyboard-aware 스크롤 처리.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (sideStep === 1) nameRef.current?.focus();
            else if (sideStep === 2) telecomSelRef.current?.open();
            else if (sideStep === 3) phoneRef.current?.focus();
        }, 300);
        return () => clearTimeout(timer);
    }, [sideStep]);

    // 이름 검증
    useEffect(() => {
        if (nameVal.trim().length === 0) {
            setIsNameValid(false);
            setNameInfoMsg(undefined);
            return;
        }
        setIsNameValid(true);
        setNameInfoMsg(undefined);
    }, [nameVal]);

    // 통신사 검증
    useEffect(() => {
        if (!telecomItem) {
            setIsTelecomValid(false);
            setTelecomInfoMsg(undefined);
            return;
        }
        setIsTelecomValid(true);
        setTelecomInfoMsg(undefined);
    }, [telecomItem]);

    // 전화번호 검증
    useEffect(() => {
        if (phoneValue.trim().length === 0) {
            setIsPhoneValid(false);
            setPhoneInfoMsg(undefined);
            return;
        }

        // 보호자 모드 + selfCtn 과 동일 번호면 invalid
        if (isParentMode && selfCtn) {
            const currentPhone = phoneValue.replace(/-/g, "");
            if (currentPhone === selfCtn) {
                setIsPhoneValid(false);
                setPhoneInfoMsg({ msg: t("signUp.step3.sameAsExistingPhone"), type: "error" });
                return;
            }
        }

        const isValid = phoneValue.trim().length >= 10;
        setIsPhoneValid(isValid);
        setPhoneInfoMsg(!isValid ? { msg: t("signUp.phoneErrorMsg"), type: "error" } : undefined);
    }, [phoneValue, isParentMode, selfCtn, t]);

    // 버튼 활성화 — 국적 라디오는 항상 기본값(KOREAN) 보유 → 검증 불필요.
    useEffect(() => {
        if (sideStep === 1) {
            setIsBtnDisabled(!isNameValid);
        } else if (sideStep === 2) {
            setIsBtnDisabled(!isNameValid || !isTelecomValid);
        } else if (sideStep === 3) {
            setIsBtnDisabled(!isNameValid || !isTelecomValid || !isPhoneValid);
        }
    }, [sideStep, isNameValid, isTelecomValid, isPhoneValid]);

    // sideStep 도중 헤더 뒤로가기로 이탈해도 입력값이 복원되도록 변경 시마다 부모에 통지
    useEffect(() => {
        onValuesChange?.({
            name: nameVal.trim(),
            telecom: telecomItem?.value || "",
            phone: phoneValue.replace(/-/g, ""),
            nationality,
        });
    }, [nameVal, telecomItem, phoneValue, nationality, onValuesChange]);

    const handleNext = useCallback(() => {
        if (sideStep === 1) {
            setSideStep(2);
        } else if (sideStep === 2) {
            setSideStep(3);
            setBtnName(t("signUp.btnSMS"));
        } else if (sideStep === 3) {
            // 사용자가 "010..." 으로 넣든 "10..." 으로 넣든 저장 형식(선행 0 없는 national number)
            //   으로 수렴시킨다. 다날이 요구하는 국내 표기는 요청 시점에 toDanalCtn 이 붙인다.
            //   onValuesChange(실시간) 는 raw 그대로 부모에 전달해 sideStep 복원 입력 흐름 유지.
            onNext({
                name: nameVal.trim(),
                telecom: telecomItem?.value || "",
                phone: toNationalNumber(phoneValue, "KR"),
                nationality,
            });
        }
    }, [sideStep, t, nameVal, telecomItem, phoneValue, nationality, onNext]);

    // 14세미만 보호자 인증은 SignUp 전용이라 강제로 titleUnder14 사용
    const titleKey = isParentMode ? "signUp.step3.titleUnder14" : titleI18nKey;

    return (
        <div className="identity-form">
            <span className="step3-title">{t(titleKey)}</span>
            <span className="step3-info">{t(infoI18nKey)}</span>

            {/* 전화번호 (sideStep 3) */}
            <div className={`identity-hide-container ${sideStep >= 3 ? "" : "hide"}`}>
                <Input
                    id="identity-phone"
                    ref={phoneRef}
                    inputVal={phoneValue}
                    setInputVal={setPhoneValue}
                    onChange={(e) => setPhoneValue(e.target.value.replace(/[^0-9]/g, ""))}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={11}
                    title={t("signUp.step3.phoneNumber")}
                    placeholder={t("signUp.step3.phoneNumberPlaceholder")}
                    infoMsg={phoneInfoMsg}
                    isRequired
                    showClearButton
                />
            </div>

            {/* 통신사 (sideStep 2) */}
            <div className={`identity-hide-container ${sideStep >= 2 ? "" : "hide"}`}>
                <Input
                    id="identity-telecom"
                    inputVal={telecomItem?.name || ""}
                    title={t("signUp.step3.telecom")}
                    readOnly
                    placeholder={t("signUp.step3.telecomPlaceHolder")}
                    onClick={() => telecomSelRef.current?.open()}
                    infoMsg={telecomInfoMsg}
                    showClearButton={false}
                    isRequired
                />
                <SelectBox
                    items={carriers}
                    ref={telecomSelRef}
                    selectedItem={telecomItem}
                    onSelect={(item) => {
                        setTelecomItem(item);
                        telecomSelRef.current?.close();
                    }}
                />
            </div>

            {/* 이름 */}
            <Input
                id="identity-name"
                ref={nameRef}
                inputVal={nameVal}
                setInputVal={setNameVal}
                title={t("signUp.name")}
                placeholder={t("signUp.namePlaceHolder")}
                infoMsg={nameInfoMsg}
                isRequired
                showClearButton
            />

            {/* 국적 — 내국인/외국인 (KR 다날 genderDigit 1~4 / 5~8 매핑). SignUp/Recover KR 경로 전용. */}
            {enableNationality && (
                <div className="identity-nationality">
                    <RadioGroup
                        name="identity-nationality"
                        items={nationalityItems}
                        value={nationality}
                        onChange={(v) => setNationality(v as Nationality)}
                        title={t("signUp.step3.nationality")}
                        isRequired
                    />
                </div>
            )}

            <div className="identity-bottom-btn">
                <Button
                    text={btnName}
                    onPress={handleNext}
                    disabled={isBtnDisabled}
                />
            </div>
        </div>
    );
};
